import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { UserAvatar } from "@/components/ui/UserAvatar";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft, Send } from "lucide-react";
import {
  useCohortDiscussions,
  useCohortDiscussionReplies,
  useCreateCohortDiscussionReply,
} from "@/hooks/useCohortDiscussions";
import { useAuth } from "@/lib/auth";
import { useToast } from "@/hooks/use-toast";
import { formatDistanceToNow } from "date-fns";
import { UserName } from "@/components/badges/UserName";

interface Props {
  discussionId: string;
  cohortId: string;
  onBack: () => void;
}

export function CohortDiscussionThread({ discussionId, cohortId, onBack }: Props) {
  const { user } = useAuth();
  const { toast } = useToast();
  const { data: discussions, isLoading: discussionLoading } = useCohortDiscussions(cohortId);
  const discussion = discussions?.find((d) => d.id === discussionId);
  const { data: replies, isLoading: repliesLoading } = useCohortDiscussionReplies(discussionId);
  const createReply = useCreateCohortDiscussionReply();
  const [replyContent, setReplyContent] = useState("");

  const handleSubmitReply = async () => {
    const trimmed = replyContent.trim();
    if (!trimmed || !user) return;
    try {
      await createReply.mutateAsync({
        discussionId,
        content: trimmed,
        userId: user.id,
      });
      setReplyContent("");
      toast({ title: "Reply posted" });
    } catch {
      toast({
        title: "Error",
        description: "Failed to post reply.",
        variant: "destructive",
      });
    }
  };

  if (discussionLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-32" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (!discussion) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground">Discussion not found</p>
        <Button variant="link" onClick={onBack}>
          Go back
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Button variant="ghost" size="sm" onClick={onBack} className="-ml-2">
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back to discussions
      </Button>

      <Card>
        <CardHeader>
          <div className="flex items-start gap-3">
            <UserAvatar avatarPath={discussion.author?.avatar_url} fullName={discussion.author?.full_name} className="h-10 w-10" fallbackClassName="bg-primary text-primary-foreground" thumbnailSize={64} />
            <div className="flex-1 min-w-0">
              <CardTitle className="text-lg">{discussion.title}</CardTitle>
              <div className="text-sm text-muted-foreground mt-1">
                <span><UserName userId={discussion.user_id} name={discussion.author?.full_name} /></span>
                <span className="mx-2">•</span>
                <span>
                  {formatDistanceToNow(new Date(discussion.created_at), { addSuffix: true })}
                </span>
              </div>
              {discussion.content && (
                <p className="mt-4 whitespace-pre-wrap text-sm">{discussion.content}</p>
              )}
            </div>
          </div>
        </CardHeader>
      </Card>

      <div className="space-y-4">
        <h4 className="font-medium">Replies ({replies?.length || 0})</h4>

        {repliesLoading ? (
          <div className="space-y-3">
            {[...Array(2)].map((_, i) => (
              <Skeleton key={i} className="h-24 w-full" />
            ))}
          </div>
        ) : replies && replies.length > 0 ? (
          <div className="space-y-3">
            {replies.map((reply) => (
              <Card key={reply.id}>
                <CardContent className="p-4">
                  <div className="flex gap-3">
                    <UserAvatar avatarPath={reply.author?.avatar_url} fullName={reply.author?.full_name} className="h-8 w-8 flex-shrink-0" fallbackClassName="bg-primary text-primary-foreground text-xs" thumbnailSize={64} />
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-muted-foreground">
                        <span className="font-medium text-foreground">
                          <UserName userId={reply.user_id} name={reply.author?.full_name} />
                        </span>
                        <span className="mx-2">•</span>
                        <span>
                          {formatDistanceToNow(new Date(reply.created_at), { addSuffix: true })}
                        </span>
                      </div>
                      <p className="mt-1 whitespace-pre-wrap text-sm">{reply.content}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No replies yet. Be the first.</p>
        )}
      </div>

      <Card>
        <CardContent className="p-4 space-y-3">
          <Textarea
            value={replyContent}
            onChange={(e) => setReplyContent(e.target.value)}
            placeholder="Write a reply..."
            rows={3}
          />
          <div className="flex justify-end">
            <Button
              onClick={handleSubmitReply}
              disabled={!replyContent.trim() || createReply.isPending}
              size="sm"
            >
              <Send className="h-4 w-4 mr-2" />
              {createReply.isPending ? "Posting..." : "Post Reply"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
